import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  LayoutDashboard, Zap, Users, ShieldCheck, Shield, ClipboardList, Settings,
  RotateCw, LogOut, Globe, Smartphone, Monitor, Crown, Ban,
  ArrowUp, ArrowDown, ArrowRight, Check, X, Clock, Paperclip, KeyRound,
  Copy, Stethoscope, AlertTriangle, Ghost, CheckCircle2, ChevronRight, Newspaper,
  Trash2, Undo2, Eye, ArrowUpCircle,
} from "lucide-react";

import MasterBlog from "./MasterBlog";
import { getToken, clearAllTokens } from "../../utils/tokenStorage";
import { useIdleTimeout } from "../../hooks/useIdleTimeout";
import IdleWarningModal from "../../components/IdleWarningModal";

// Aurora token aliases (kept as a thin map so existing T.* references restyle 1:1).
// All point at the global aurora.css CSS variables — default/dark (aegis) theme.
const T = {
  bg: "var(--au-bg)",
  sidebar: "var(--au-bg-elevated)",
  card: "var(--au-glass)",
  cardHover: "var(--au-glass-strong)",
  border: "var(--au-border)",
  borderAccent: "var(--au-hair)",
  text: "var(--au-text)",
  textSec: "var(--au-text-2)",
  textMuted: "var(--au-muted)",
  accent: "var(--au-accent)",
  accentDim: "var(--au-glass-strong)",
  live: "var(--au-success)",
  info: "var(--au-info)",
  danger: "var(--au-error)",
  warning: "var(--au-warn)",
  fontMono: "var(--au-font-mono)",
};

// recharts renders to canvas/SVG and cannot read CSS vars, so chart props use
// literal hexes that match the dark aegis Aurora theme.
const CHART = {
  accent: "#1FD0A3",
  info: "#4DA3E0",
  warn: "#F4BE50",
  error: "#F2503C",
  success: "#29BF12",
  text: "#C8CFD8",
  muted: "#8A93A0",
  grid: "rgba(255,255,255,0.09)",
  panel: "#0E0E0E",
};

const masterApi = (path, opts = {}) => {
  const token = getToken("master_token");
  return fetch(`/api/master${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  }).then(async (r) => {
    if (r.status === 401) {
      clearAllTokens();
      window.location.href = "/mascop-2k26";
      return;
    }
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || "Request failed");
    }
    return r.json();
  });
};

const timeAgo = (ts) => {
  if (!ts) return "—";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 1) return "just now";
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const flag = (cc) => {
  if (!cc || cc.length !== 2) return <Globe size={15} style={{ opacity: 0.8 }} />;
  const codePoints = cc.toUpperCase().split("").map((c) => 127397 + c.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

const DeviceIcon = ({ type, size = 14 }) => {
  if (type === "mobile" || type === "tablet") return <Smartphone size={size} />;
  return <Monitor size={size} />;
};

const Avatar = ({ name, email, size = 36 }) => {
  const letter = (name || email || "?")[0].toUpperCase();
  const colors = ["#F2503C", "#1FD0A3", "#F4BE50", "#4DA3E0", "#29BF12", "#5BE8C4", "#43A6E4"];
  const colorIdx = (email || "x").charCodeAt(0) % colors.length;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `linear-gradient(135deg, ${colors[colorIdx]}, ${colors[(colorIdx + 2) % colors.length]})`,
      color: "#06140A", fontWeight: 700, fontSize: size * 0.42,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
    }}>{letter}</div>
  );
};

const NavItem = ({ icon, label, active, count, dot, onClick, collapsed }) => (
  <div onClick={onClick} style={{
    display: "flex", alignItems: "center", gap: 12,
    padding: collapsed ? "11px 0" : "11px 16px",
    justifyContent: collapsed ? "center" : "flex-start",
    cursor: "pointer", borderRadius: 8,
    background: active ? T.accentDim : "transparent",
    color: active ? T.accent : T.textSec,
    borderLeft: active ? `2px solid ${T.accent}` : "2px solid transparent",
    transition: "all 0.15s ease",
    fontSize: 14, fontWeight: active ? 600 : 500,
    margin: "1px 8px", position: "relative",
  }}
    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "var(--au-glass)"; }}
    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    title={collapsed ? label : ""}
  >
    <span style={{ display: "inline-flex", lineHeight: 1 }}>{icon}</span>
    {!collapsed && (
      <>
        <span style={{ flex: 1 }}>{label}</span>
        {count > 0 && (
          <span style={{
            background: T.accent, color: "var(--au-btn-text)", fontSize: 10, fontWeight: 700,
            padding: "2px 7px", borderRadius: 10, minWidth: 20, textAlign: "center",
          }}>{count}</span>
        )}
      </>
    )}
    {dot && (
      <span style={{
        position: "absolute", top: 8, right: collapsed ? 8 : 12,
        width: 8, height: 8, borderRadius: "50%",
        background: T.live, boxShadow: `0 0 0 2px ${T.sidebar}`,
        animation: "pulse 2s infinite",
      }}/>
    )}
  </div>
);

const StatCard = ({ label, value, trend, sub, accent = T.info, icon }) => {
  const trendColor = trend > 0 ? T.live : trend < 0 ? T.danger : T.textMuted;
  return (
    <div className="au-glass" style={{
      padding: "20px 22px", position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: -8, right: -8,
        width: 64, height: 64, borderRadius: "50%",
        background: `radial-gradient(circle, ${accent}, transparent 70%)`,
        opacity: 0.12,
      }}/>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <span className="au-stat-lbl">{label}</span>
        {icon && <span style={{ display: "inline-flex", opacity: 0.7, color: T.textSec }}>{icon}</span>}
      </div>
      <div className="au-stat-num au-mono" style={{ lineHeight: 1, marginBottom: 8 }}>{value ?? 0}</div>
      {(trend !== undefined || sub) && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
          {trend !== undefined && trend !== null && (
            <span style={{ color: trendColor, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 3 }}>
              {trend > 0 ? <ArrowUp size={12} /> : trend < 0 ? <ArrowDown size={12} /> : <ArrowRight size={12} />} {Math.abs(trend)}%
            </span>
          )}
          {sub && <span style={{ color: T.textMuted }}>{sub}</span>}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, v }) => (
  <div>
    <div style={{
      fontSize: 10, color: T.textMuted, letterSpacing: 1.5,
      marginBottom: 4, textTransform: "uppercase", fontWeight: 600,
    }}>{label}</div>
    <div style={{ fontSize: 13, color: T.text }}>{v || "—"}</div>
  </div>
);

export default function MasterDashboard() {
  const [showIdleModal, setShowIdleModal] = React.useState(false);
  const handleMasterIdleLogout = React.useCallback(() => {
    try { sessionStorage.removeItem("master_token"); localStorage.removeItem("master_token"); } catch(e) {}
    window.location.href = "/mascop-2k26";
  }, []);
  const masterIdle = useIdleTimeout({
    timeoutMinutes: 15,
    warningMinutes: 1,
    onIdle: handleMasterIdleLogout,
    enabled: true
  });

  const [tab, setTab] = useState("overview");
  const [collapsed, setCollapsed] = useState(false);
  const [data, setData] = useState({
    stats: null, summary: null, liveStats: null,
    daily: [], plans: [], risk: [], activity: [],
    pendingKyc: [], users: [], scans: [], audit: [],
    liveSessions: [], removedUsers: [],
  });
  const [busy, setBusy] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [search, setSearch] = useState("");
  const [userSubTab, setUserSubTab] = useState("active");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [expandedUser, setExpandedUser] = useState(null);
  const [loginHistory, setLoginHistory] = useState({});
  const nav = useNavigate();

  const loadAll = useCallback(async (showToast = false) => {
    try {
      const [
        stats, summary, liveStats, daily, plans, risk,
        activity, kyc, users, scans, audit, liveSessions, removed
      ] = await Promise.all([
        masterApi("/stats"),
        masterApi("/analytics/summary"),
        masterApi("/stats/live").catch(() => ({ online_now: 0, active_24h: 0 })),
        masterApi("/analytics/daily?days=14"),
        masterApi("/analytics/plans"),
        masterApi("/analytics/risk"),
        masterApi("/analytics/activity?limit=20"),
        masterApi("/kyc/pending"),
        masterApi("/users?limit=50"),
        masterApi("/scans?limit=20"),
        masterApi("/audit?limit=30"),
        masterApi("/sessions/live").catch(() => ({ count: 0, sessions: [] })),
        masterApi("/users/removed").catch(() => ({ users: [] })),
      ]);
      setData({
        stats, summary, liveStats, plans, risk, activity,
        daily: daily.daily || [],
        pendingKyc: kyc, users: users.users || [],
        scans, audit,
        liveSessions: liveSessions.sessions || [],
        removedUsers: removed.users || [],
      });
      setLastUpdate(new Date());
      if (showToast) toast.success("Refreshed", { duration: 1200 });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }, []);

  const refreshLiveOnly = useCallback(async () => {
    try {
      const [liveStats, liveSessions] = await Promise.all([
        masterApi("/stats/live"),
        masterApi("/sessions/live"),
      ]);
      setData((d) => ({ ...d, liveStats, liveSessions: liveSessions.sessions || [] }));
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!getToken("master_token")) {
      nav("/mascop-2k26");
      return;
    }
    loadAll();
    const fullRefresh = setInterval(() => loadAll(), 30000);
    return () => clearInterval(fullRefresh);
  }, []);

  useEffect(() => {
    if (tab !== "live") return;
    const tick = setInterval(refreshLiveOnly, 10000);
    return () => clearInterval(tick);
  }, [tab, refreshLiveOnly]);

  const approveKyc = async (kyc_id, email) => {
    if (!window.confirm(`Approve KYC for ${email}?`)) return;
    try {
      await masterApi("/kyc/approve", { method: "POST", body: JSON.stringify({ kyc_id }) });
      toast.success("KYC approved"); loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const rejectKyc = async (kyc_id, email) => {
    const reason = window.prompt(`Rejection reason for ${email}:`);
    if (!reason) return;
    try {
      await masterApi("/kyc/reject", { method: "POST", body: JSON.stringify({ kyc_id, reason }) });
      toast.success("Rejected"); loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const banUser = async (id, email) => {
    if (!window.confirm(`Ban ${email}?`)) return;
    try {
      await masterApi("/users/ban", { method: "POST", body: JSON.stringify({ user_id: id }) });
      toast.success("Banned"); loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const unbanUser = async (id, email) => {
    try {
      await masterApi("/users/unban", { method: "POST", body: JSON.stringify({ user_id: id }) });
      toast.success("Unbanned"); loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const removeUser = async (id, email) => {
    if (!window.confirm(`Remove ${email}?\n\nThey'll be blocked from the app and moved to the Removed section. You can restore them later.`)) return;
    try {
      await masterApi("/users/remove", { method: "POST", body: JSON.stringify({ user_id: id }) });
      toast.success("User removed"); loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const restoreUser = async (id, email) => {
    try {
      await masterApi("/users/restore", { method: "POST", body: JSON.stringify({ user_id: id }) });
      toast.success("User restored"); loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const upgradeUser = async (id, email) => {
    const plan = window.prompt("Plan: FREE / PAY_SCAN / STARTER / PRO / PRO_PLUS / AGENCY");
    if (!plan) return;
    try {
      await masterApi("/users/upgrade", {
        method: "POST",
        body: JSON.stringify({ user_id: id, plan: plan.toUpperCase().trim() })
      });
      toast.success(`Upgraded to ${plan}`); loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const logout = () => {
    clearAllTokens();
    nav("/mascop-2k26");
  };

  const viewDocument = async (kyc_id) => {
    try {
      const res = await fetch(`/api/master/kyc/${kyc_id}/document`, {
        headers: { Authorization: `Bearer ${getToken("master_token")}` }
      });
      if (!res.ok) throw new Error("Failed to load (status " + res.status + ")");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Document load failed: " + e.message); }
  };

  const toggleUserExpand = async (userId) => {
    if (expandedUser === userId) { setExpandedUser(null); return; }
    setExpandedUser(userId);
    if (!loginHistory[userId]) {
      try {
        const hist = await masterApi(`/users/${userId}/login-history?limit=20`);
        setLoginHistory((h) => ({ ...h, [userId]: hist }));
      } catch (e) { toast.error("Login history failed: " + e.message); }
    }
  };

  if (busy) {
    return (
      <div className="au-root" style={{
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 48, height: 48, borderRadius: "50%",
            border: `3px solid ${T.border}`, borderTopColor: T.accent,
            animation: "spin 0.8s linear infinite", margin: "0 auto 20px",
          }}/>
          <div className="au-mono" style={{
            color: T.accent, fontSize: 12, letterSpacing: 4, fontWeight: 600,
          }}>LOADING COMMAND CENTER</div>
        </div>
        <style>{`@keyframes spin { from {transform:rotate(0);} to {transform:rotate(360deg);} }`}</style>
      </div>
    );
  }

  const {
    stats, summary, liveStats, daily, plans, risk,
    activity, pendingKyc, users, scans, audit, liveSessions
  } = data;

  const matchesSearch = (u) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (u.email || "").toLowerCase().includes(s)
        || (u.name || "").toLowerCase().includes(s);
  };
  const activeUsers = users.filter((u) => !u.is_banned);
  const bannedUsers = users.filter((u) => u.is_banned);
  const removedUsers = data.removedUsers || [];
  const userBuckets = { active: activeUsers, banned: bannedUsers, removed: removedUsers };
  const filteredUsers = (userBuckets[userSubTab] || activeUsers).filter(matchesSearch);
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };
  const sortVal = (u, k) =>
    k === "user" ? (u.email || "").toLowerCase() :
    k === "plan" ? String(u.plan || "") :
    k === "scans" ? (u.scans_used ?? 0) :
    k === "seen" ? (u.last_activity_at ? new Date(u.last_activity_at).getTime() : 0) : 0;
  const sortedUsers = sortKey
    ? [...filteredUsers].sort((a, b) => {
        const va = sortVal(a, sortKey), vb = sortVal(b, sortKey);
        const c = va < vb ? -1 : va > vb ? 1 : 0;
        return sortDir === "asc" ? c : -c;
      })
    : filteredUsers;
  const sortTh = (label, k, right) => (
    <span onClick={() => toggleSort(k)} style={{
      cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
      justifyContent: right ? "flex-end" : "flex-start", userSelect: "none",
      color: sortKey === k ? T.accent : undefined,
    }}>
      {label}{sortKey === k && (sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
    </span>
  );

  const onlineNow = liveStats?.online_now || 0;

  const tabs = [
    { id: "overview", icon: <LayoutDashboard size={18} />, label: "Overview" },
    { id: "live", icon: <Zap size={18} />, label: "Live Sessions", count: onlineNow, dot: onlineNow > 0 },
    { id: "users", icon: <Users size={18} />, label: "Users", count: stats?.users?.total || 0 },
    { id: "kyc", icon: <ShieldCheck size={18} />, label: "KYC Queue", count: pendingKyc.length },
    { id: "scans", icon: <Shield size={18} />, label: "Scans" },
    { id: "blog", icon: <Newspaper size={18} />, label: "Blog" },
    { id: "audit", icon: <ClipboardList size={18} />, label: "Audit Log" },
    { id: "settings", icon: <Settings size={18} />, label: "Settings" },
  ];

  const currentTabLabel = tabs.find((t) => t.id === tab)?.label || "";

  return (
    <div className="au-root" style={{ color: T.text }}>
      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.5; transform:scale(1.2); } }
        @keyframes spin { from { transform:rotate(0); } to { transform:rotate(360deg); } }
        @keyframes slideIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes glow { 0%,100% { box-shadow:0 0 8px var(--au-success); } 50% { box-shadow:0 0 16px var(--au-success); } }
        ::-webkit-scrollbar { width:8px; height:8px; }
        ::-webkit-scrollbar-track { background:var(--au-bg); }
        ::-webkit-scrollbar-thumb { background:var(--au-hair); border-radius:4px; }
        ::-webkit-scrollbar-thumb:hover { background:var(--au-muted); }
        .row-hover:hover { background:var(--au-glass-strong) !important; }
        .live-dot { animation: glow 1.5s infinite; }
        .anim-in { animation: slideIn 0.3s ease; }
      `}</style>

      <aside style={{
        position: "fixed", left: 0, top: 0, bottom: 0,
        width: collapsed ? 64 : 240,
        background: T.sidebar,
        borderRight: `1px solid ${T.border}`,
        display: "flex", flexDirection: "column",
        transition: "width 0.2s ease", zIndex: 10,
      }}>
        <div style={{
          padding: collapsed ? "20px 0" : "20px 20px",
          borderBottom: `1px solid ${T.border}`,
          display: "flex", alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
        }}>
          {!collapsed ? (
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 4, color: T.text, lineHeight: 1 }}>AEGIS</div>
              <div style={{ fontSize: 9, color: T.accent, letterSpacing: 3, marginTop: 4, fontWeight: 600 }}>COMMAND CENTER</div>
            </div>
          ) : (
            <div style={{ fontSize: 18, fontWeight: 700, color: T.accent, letterSpacing: 1 }}>A</div>
          )}
        </div>

        <nav style={{ flex: 1, padding: "12px 0", overflowY: "auto" }}>
          {tabs.map((t) => (
            <NavItem key={t.id} icon={t.icon} label={t.label}
              count={t.count} dot={t.dot}
              active={tab === t.id}
              onClick={() => setTab(t.id)}
              collapsed={collapsed}/>
          ))}
        </nav>

        <div style={{
          padding: collapsed ? "12px 0" : "16px 12px",
          borderTop: `1px solid ${T.border}`,
        }}>
          {!collapsed ? (
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px", borderRadius: 8,
              background: T.accentDim, marginBottom: 8,
            }}>
              <Avatar name="aegismaster" email="master" size={32}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>aegismaster</div>
                <div style={{ fontSize: 10, color: T.accent, letterSpacing: 1 }}>OWNER · GOD MODE</div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
              <Avatar name="A" email="m" size={28}/>
            </div>
          )}

          <button onClick={() => setCollapsed(!collapsed)} style={{
            width: "100%", background: "transparent",
            border: `1px solid ${T.border}`, color: T.textSec,
            padding: "8px", borderRadius: 6, cursor: "pointer", fontSize: 14,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>{collapsed ? <ArrowRight size={16} /> : <ArrowLeftGlyph />}</button>
        </div>
      </aside>

      <main style={{ marginLeft: collapsed ? 64 : 240, transition: "margin-left 0.2s ease" }}>
        <header style={{
          height: 64, background: T.bg,
          borderBottom: `1px solid ${T.border}`,
          padding: "0 28px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, zIndex: 5,
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text, letterSpacing: 0.3 }}>{currentTabLabel}</h1>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Last refreshed {timeAgo(lastUpdate)}</div>
          </div>

          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div className="au-chip au-chip-status" style={{
              gap: 8, padding: "7px 14px",
            }}>
              <span className={onlineNow > 0 ? "au-led live-dot" : "au-led au-led-off"} />
              <span style={{ fontSize: 12, fontWeight: 600, color: onlineNow > 0 ? T.live : T.textSec }}>{onlineNow} online now</span>
            </div>

            <button onClick={() => loadAll(true)} className="au-pill au-pill-glass au-pill-sm">
              <RotateCw size={14} /> Refresh
            </button>

            <button onClick={logout} className="au-pill au-pill-sm" style={{
              background: "var(--au-glass-strong)", border: `1px solid ${T.danger}`,
              color: T.danger, fontWeight: 600,
            }}>
              <LogOut size={14} /> Logout
            </button>
          </div>
        </header>

        <div style={{ padding: "28px" }} className="anim-in" key={tab}>

          {tab === "overview" && (
            <>
              <div style={{
                display: "grid", gap: 16,
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                marginBottom: 24,
              }}>
                <StatCard label="Total Users" value={stats?.users?.total} icon={<Users size={16} />}
                  trend={summary?.users?.week_trend} sub="this week" accent={CHART.info}/>
                <StatCard label="Online Now" value={onlineNow} icon={<Zap size={16} />}
                  sub={`${liveStats?.active_24h || 0} active last 24h`} accent={CHART.success}/>
                <StatCard label="Scans Today" value={summary?.scans?.today} icon={<Shield size={16} />}
                  trend={summary?.scans?.today_trend} sub="vs yesterday" accent={CHART.accent}/>
                <StatCard label="Pending KYC" value={pendingKyc.length} icon={<ShieldCheck size={16} />}
                  sub="requires review" accent={CHART.warn}/>
              </div>

              <div style={{ display: "grid", gap: 16, gridTemplateColumns: "2fr 1fr", marginBottom: 16 }}>
                <ChartCard title="Daily Traffic — Last 14 Days">
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={daily}>
                      <defs>
                        <linearGradient id="userG" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART.info} stopOpacity={0.5}/>
                          <stop offset="100%" stopColor={CHART.info} stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="scanG" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART.success} stopOpacity={0.5}/>
                          <stop offset="100%" stopColor={CHART.success} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid}/>
                      <XAxis dataKey="label" stroke={CHART.muted} fontSize={11}/>
                      <YAxis stroke={CHART.muted} fontSize={11}/>
                      <Tooltip contentStyle={{ background: CHART.panel, border: `1px solid ${CHART.grid}`, borderRadius: 8, color: CHART.text, fontSize: 12 }}/>
                      <Legend wrapperStyle={{ fontSize: 12, color: CHART.text }}/>
                      <Area type="monotone" dataKey="users" stroke={CHART.info} fill="url(#userG)" strokeWidth={2} name="New Users"/>
                      <Area type="monotone" dataKey="scans" stroke={CHART.success} fill="url(#scanG)" strokeWidth={2} name="Scans"/>
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Risk Distribution">
                  {risk.some((r) => r.value > 0) ? (
                    <RiskDonut data={risk} />
                  ) : <EmptyState text="No scan data yet"/>}
                </ChartCard>
              </div>

              <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
                <ChartCard title="Plan Distribution">
                  {plans.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={plans} layout="vertical" margin={{ left: 4, right: 12 }}>
                        <defs>
                          <linearGradient id="planBar" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor={CHART.accent} stopOpacity={0.5} />
                            <stop offset="100%" stopColor={CHART.accent} stopOpacity={1} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} horizontal={false}/>
                        <XAxis type="number" stroke={CHART.muted} fontSize={10} allowDecimals={false}/>
                        <YAxis dataKey="plan" type="category" stroke={CHART.muted} fontSize={10} width={78}
                          tickFormatter={(v) => String(v).split(".").pop().replace(/_/g, " ")}/>
                        <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }}
                          contentStyle={{ background: CHART.panel, border: `1px solid ${CHART.grid}`, borderRadius: 8, color: CHART.text, fontSize: 12 }}
                          labelFormatter={(l) => String(l).split(".").pop().replace(/_/g, " ")}
                          formatter={(v) => [v, "Users"]}/>
                        <Bar dataKey="count" fill="url(#planBar)" radius={[0, 5, 5, 0]} maxBarSize={22}/>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No data"/>}
                </ChartCard>

                <ChartCard title="Blocked Attempts">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={daily}>
                      <defs>
                        <linearGradient id="blockBar" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART.error} stopOpacity={0.95} />
                          <stop offset="100%" stopColor={CHART.error} stopOpacity={0.35} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false}/>
                      <XAxis dataKey="label" stroke={CHART.muted} fontSize={10}/>
                      <YAxis stroke={CHART.muted} fontSize={10} allowDecimals={false}/>
                      <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }}
                        contentStyle={{ background: CHART.panel, border: `1px solid ${CHART.grid}`, borderRadius: 8, color: CHART.text, fontSize: 12 }}
                        formatter={(v) => [v, "Blocked"]}/>
                      <Bar dataKey="blocked" fill="url(#blockBar)" radius={[4, 4, 0, 0]} maxBarSize={30}/>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                <div className="au-glass" style={{ padding: 0, overflow: "hidden" }}>
                  <div style={{ padding: "16px 18px", borderBottom: `1px solid ${T.border}`, fontSize: 13, fontWeight: 600, color: T.text }}>Activity Feed</div>
                  <div style={{ maxHeight: 200, overflowY: "auto" }}>
                    {activity.length === 0 ? <EmptyState text="No activity yet"/> :
                      activity.map((a, i) => (
                        <div key={i} style={{
                          display: "flex", alignItems: "center", padding: "10px 18px",
                          borderBottom: `1px solid ${T.border}`, fontSize: 12,
                        }}>
                          <span style={{ display: "inline-flex", marginRight: 10, color: a.color }}>
                            <ActivityDot color={a.color} />
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: a.color, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.title}</div>
                            <div style={{ color: T.textMuted, fontSize: 10, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.subtitle}</div>
                          </div>
                          <span className="au-mono" style={{ color: T.textMuted, fontSize: 10, marginLeft: 8 }}>{timeAgo(a.time)}</span>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </>
          )}

          {tab === "live" && (
            <>
              <div className="au-glass" style={{
                padding: "16px 22px", marginBottom: 20,
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div>
                  <div style={{ fontSize: 13, color: T.textSec }}>Real-time view of users active in the last 2 minutes</div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Auto-refreshes every 10 seconds</div>
                </div>
                <div className="au-chip au-chip-status" style={{
                  gap: 10, padding: "10px 18px",
                }}>
                  <span className="au-led live-dot" style={{ width: 10, height: 10 }} />
                  <span style={{ fontSize: 14, color: T.live, fontWeight: 700, letterSpacing: 1 }}>{liveSessions.length} LIVE</span>
                </div>
              </div>

              {liveSessions.length === 0 ? (
                <div className="au-glass" style={{
                  padding: 60, textAlign: "center",
                }}>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, opacity: 0.5, color: T.textMuted }}>
                    <Ghost size={48} />
                  </div>
                  <div style={{ fontSize: 16, color: T.text, fontWeight: 600 }}>No one online right now</div>
                  <div style={{ fontSize: 13, color: T.textMuted, marginTop: 8 }}>
                    Users will appear here within 30 seconds of activity
                  </div>
                </div>
              ) : (
                <div className="au-glass" style={{ padding: 0, overflow: "hidden" }}>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(220px,1.8fr) minmax(140px,1.2fr) minmax(160px,1.3fr) minmax(160px,1.3fr) minmax(110px,1fr)",
                    padding: "14px 20px",
                    background: "var(--au-glass)",
                    borderBottom: `1px solid ${T.border}`,
                    fontSize: 10, color: T.textMuted, fontWeight: 700,
                    letterSpacing: 1.5, textTransform: "uppercase", gap: 12,
                  }}>
                    <span>User</span><span>Current Page</span><span>Location</span><span>Device</span>
                    <span style={{ textAlign: "right" }}>Activity</span>
                  </div>

                  {liveSessions.map((s) => (
                    <div key={s.user_id} className="row-hover" style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(220px,1.8fr) minmax(140px,1.2fr) minmax(160px,1.3fr) minmax(160px,1.3fr) minmax(110px,1fr)",
                      padding: "14px 20px",
                      borderBottom: `1px solid ${T.border}`,
                      alignItems: "center", gap: 12,
                      transition: "background 0.15s ease",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <Avatar name={s.name} email={s.email} size={36}/>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name || s.email.split("@")[0]}</div>
                          <div style={{ fontSize: 11, color: T.textSec, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.email}</div>
                        </div>
                      </div>

                      <div>
                        <div className="au-mono" style={{
                          display: "inline-block", padding: "4px 10px",
                          background: T.accentDim, color: T.accent,
                          fontSize: 11, fontWeight: 600,
                          borderRadius: 4, maxWidth: "100%",
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}>{s.current_page || "/"}</div>
                        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4 }}>{s.plan}</div>
                      </div>

                      <div>
                        <div style={{ fontSize: 13, color: T.text, display: "flex", gap: 6, alignItems: "center" }}>
                          <span style={{ fontSize: 16, display: "inline-flex", alignItems: "center" }}>{flag(s.country_code)}</span>
                          <span>{s.city || "Unknown"}</span>
                        </div>
                        <div className="au-mono" style={{ fontSize: 10, color: T.textMuted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.ip_address || "—"}</div>
                      </div>

                      <div>
                        <div style={{ fontSize: 12, color: T.text, display: "flex", gap: 6, alignItems: "center" }}>
                          <DeviceIcon type={s.device_type} size={14} />
                          <span>{s.browser || "?"} · {s.os || "?"}</span>
                        </div>
                        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.isp || "—"}</div>
                      </div>

                      <div style={{ textAlign: "right" }}>
                        <div style={{
                          fontSize: 12, fontWeight: 600,
                          color: (s.idle_seconds ?? 999) < 30 ? T.live :
                                 (s.idle_seconds ?? 999) < 60 ? T.warning : T.textSec,
                        }}>
                          {s.idle_seconds < 5 ? "● Active now" :
                           s.idle_seconds < 60 ? `${s.idle_seconds}s idle` :
                           `${Math.floor((s.idle_seconds || 0) / 60)}m idle`}
                        </div>
                        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>Session: {timeAgo(s.session_started)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "users" && (
            <>
              {/* Sub-sections: Active / Banned / Removed */}
              <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                {[
                  { id: "active", label: "Active", icon: <Users size={13} />, count: activeUsers.length, color: T.accent },
                  { id: "banned", label: "Banned", icon: <Ban size={13} />, count: bannedUsers.length, color: T.danger },
                  { id: "removed", label: "Removed", icon: <Trash2 size={13} />, count: removedUsers.length, color: T.textMuted },
                ].map((s) => {
                  const on = userSubTab === s.id;
                  return (
                    <button key={s.id} onClick={() => setUserSubTab(s.id)} style={{
                      display: "inline-flex", alignItems: "center", gap: 7,
                      padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                      background: on ? T.accentDim : "var(--au-glass)",
                      border: `1px solid ${on ? s.color : T.border}`,
                      color: on ? s.color : T.textSec,
                      fontSize: 13, fontWeight: on ? 700 : 500, letterSpacing: 0.3,
                      transition: "all 0.15s ease",
                    }}>
                      {s.icon}{s.label}
                      <span style={{
                        background: on ? s.color : "var(--au-glass-strong)",
                        color: on ? "var(--au-btn-text)" : T.textMuted,
                        fontSize: 10, fontWeight: 700, padding: "1px 7px",
                        borderRadius: 10, minWidth: 18, textAlign: "center",
                      }}>{s.count}</span>
                    </button>
                  );
                })}
              </div>

              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginBottom: 16, gap: 12, flexWrap: "wrap",
              }}>
                <input type="text" value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by email or name…"
                  className="au-input"
                  style={{ width: 340, maxWidth: "100%" }}/>
                <div style={{ fontSize: 12, color: T.textMuted }}>
                  {filteredUsers.length} {userSubTab} {filteredUsers.length === 1 ? "user" : "users"}
                </div>
              </div>

              <div className="au-glass" style={{ overflowX: "auto", padding: 0 }}>
              <div style={{ minWidth: 1080 }}>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(220px,2fr) minmax(100px,1fr) minmax(90px,0.8fr) minmax(80px,0.7fr) minmax(110px,0.9fr) minmax(170px,1.1fr) minmax(160px,1fr)",
                  padding: "14px 20px",
                  background: "var(--au-glass)",
                  borderBottom: `1px solid ${T.border}`,
                  fontSize: 10, color: T.textMuted, fontWeight: 700,
                  letterSpacing: 1.5, textTransform: "uppercase", gap: 10,
                }}>
                  {sortTh("User", "user")}{sortTh("Plan", "plan")}<span>Email</span><span>KYC</span>{sortTh("Scans", "scans")}{sortTh("Last Seen", "seen")}
                  <span style={{ textAlign: "right" }}>Actions</span>
                </div>

                {sortedUsers.map((u) => (
                  <React.Fragment key={u.id}>
                    <div className="row-hover" onClick={() => toggleUserExpand(u.id)} style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(220px,2fr) minmax(100px,1fr) minmax(90px,0.8fr) minmax(80px,0.7fr) minmax(110px,0.9fr) minmax(170px,1.1fr) minmax(160px,1fr)",
                      padding: "14px 20px",
                      borderBottom: `1px solid ${T.border}`,
                      alignItems: "center", gap: 10,
                      cursor: "pointer", transition: "background 0.15s ease",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <span style={{
                          color: T.textMuted,
                          transition: "transform 0.2s",
                          transform: expandedUser === u.id ? "rotate(90deg)" : "rotate(0)",
                          display: "inline-flex", width: 12,
                        }}><ChevronRight size={12} /></span>
                        <Avatar name={u.name} email={u.email} size={32}/>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: T.text, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
                          <div style={{ fontSize: 11, color: T.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.name || "—"}</div>
                        </div>
                      </div>

                      <span style={{ fontSize: 11, fontWeight: 700, color: T.accent, textTransform: "uppercase", letterSpacing: 0.5 }}>{String(u.plan).split(".").pop()}</span>

                      <span>
                        {u.is_verified
                          ? <Badge color={T.live} icon={<Check size={11} />} text="Verified"/>
                          : <Badge color={T.warning} text="Pending"/>}
                      </span>

                      <span>
                        {u.is_admin ? <Badge color={CHART.warn} icon={<Crown size={11} />} text="Admin"/> :
                         u.is_banned ? <Badge color={T.danger} icon={<Ban size={11} />} text="Banned"/> :
                         u.kyc_status === "approved" ? <Badge color={T.live} icon={<Check size={11} />} text=""/> :
                         u.kyc_status === "pending" ? <Badge color={T.warning} icon={<Clock size={11} />} text=""/> :
                         u.kyc_status === "rejected" ? <Badge color={T.danger} icon={<X size={11} />} text=""/> :
                         <Badge color={T.textMuted} text="—"/>}
                      </span>

                      <span className="au-mono" style={{ fontSize: 12, color: T.text }}>
                        {u.scans_used}<span style={{ color: T.textMuted }}>/{u.scans_limit > 0 ? u.scans_limit : (u.plan_quota ?? "—")}</span>
                      </span>

                      <span style={{ fontSize: 11, color: T.textSec }}>
                        {u.last_activity_at ? (
                          <>
                            <div style={{ color: T.text, fontWeight: 500 }}>{timeAgo(u.last_activity_at)}</div>
                            {u.last_ip && <div className="au-mono" style={{ fontSize: 10, color: T.textMuted }}>{u.last_ip}</div>}
                          </>
                        ) : <span style={{ color: T.textMuted }}>Never</span>}
                      </span>

                      <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                        <SmallBtn onClick={() => window.location.href = `/mascop-2k26/user/${u.id}`} color={T.info} title="View"><Eye size={15} /></SmallBtn>
                        {userSubTab === "removed" ? (
                          <SmallBtn onClick={() => restoreUser(u.id, u.email)} color={T.live} title="Restore"><Undo2 size={15} /></SmallBtn>
                        ) : (
                          <>
                            {u.is_banned ?
                              <SmallBtn onClick={() => unbanUser(u.id, u.email)} color={T.live} title="Unban"><ShieldCheck size={15} /></SmallBtn> :
                              <SmallBtn onClick={() => banUser(u.id, u.email)} color={T.danger} title="Ban"><Ban size={15} /></SmallBtn>}
                            <SmallBtn onClick={() => upgradeUser(u.id, u.email)} color={T.accent} title="Change plan"><ArrowUpCircle size={15} /></SmallBtn>
                            {!u.is_admin && (
                              <SmallBtn onClick={() => removeUser(u.id, u.email)} color={T.danger} title="Remove"><Trash2 size={15} /></SmallBtn>
                            )}
                          </>
                        )}
                      </span>
                    </div>

                    {expandedUser === u.id && (
                      <div className="anim-in" style={{
                        background: "var(--au-glass)",
                        borderBottom: `1px solid ${T.border}`,
                        padding: "20px 60px",
                      }}>
                        <div style={{
                          fontSize: 11, color: T.textMuted, fontWeight: 700,
                          letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12,
                        }}>Login History</div>

                        {!loginHistory[u.id] ? (
                          <div style={{ fontSize: 12, color: T.textMuted, padding: 12 }}>Loading…</div>
                        ) : loginHistory[u.id].length === 0 ? (
                          <div style={{ fontSize: 12, color: T.textMuted, padding: 12 }}>No login history yet (heartbeat will start tracking soon)</div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {loginHistory[u.id].map((evt) => (
                              <div key={evt.id} style={{
                                display: "grid",
                                gridTemplateColumns: "20px 1fr 1.2fr 1.2fr 0.8fr 0.8fr",
                                gap: 14, alignItems: "center", padding: "10px 14px",
                                background: T.card, borderRadius: 6,
                                border: `1px solid ${T.border}`, fontSize: 12,
                              }}>
                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: evt.is_active ? T.live : T.textMuted }}/>
                                <div>
                                  <div className="au-mono" style={{ color: T.text, fontSize: 12 }}>{evt.ip_address}</div>
                                  <div style={{ color: T.textMuted, fontSize: 10 }}>{evt.isp || "Unknown ISP"}</div>
                                </div>
                                <div>
                                  <div style={{ color: T.text, display: "flex", gap: 6, alignItems: "center" }}>
                                    <span style={{ fontSize: 14, display: "inline-flex", alignItems: "center" }}>{flag(evt.country_code)}</span>
                                    <span>{evt.city || "?"}</span>
                                  </div>
                                  <div style={{ color: T.textMuted, fontSize: 10 }}>{evt.region || ""} {evt.country || ""}</div>
                                </div>
                                <div>
                                  <div style={{ color: T.text, display: "flex", gap: 6, alignItems: "center" }}><DeviceIcon type={evt.device_type} size={13} /> {evt.browser}</div>
                                  <div style={{ color: T.textMuted, fontSize: 10 }}>{evt.os}</div>
                                </div>
                                <div style={{ color: T.textSec, fontSize: 11 }}>First: {timeAgo(evt.first_seen)}</div>
                                <div style={{ color: T.textSec, fontSize: 11, textAlign: "right" }}>Last: {timeAgo(evt.last_seen)}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}
                {filteredUsers.length === 0 && (
                  <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontSize: 13 }}>
                    {userSubTab === "removed" ? "No removed users" : userSubTab === "banned" ? "No banned users" : "No users found"}
                  </div>
                )}
              </div>
              </div>
            </>
          )}

          {tab === "kyc" && (
            <>
              {pendingKyc.length === 0 ? (
                <div className="au-glass" style={{ padding: 60, textAlign: "center" }}>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, color: T.live }}>
                    <CheckCircle2 size={48} />
                  </div>
                  <div style={{ fontSize: 16, color: T.live, fontWeight: 600 }}>All caught up</div>
                  <div style={{ fontSize: 13, color: T.textMuted, marginTop: 8 }}>No pending KYC reviews</div>
                </div>
              ) : pendingKyc.map((k) => (
                <div key={k.kyc_id} className="au-glass" style={{
                  padding: 24, marginBottom: 16,
                }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    marginBottom: 18, paddingBottom: 14,
                    borderBottom: `1px solid ${T.border}`,
                  }}>
                    <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                      <Avatar name={k.full_name} email={k.user_email} size={44}/>
                      <div>
                        <div style={{ fontSize: 17, fontWeight: 700, color: T.text }}>{k.full_name}</div>
                        <div style={{ fontSize: 12, color: T.accent }}>{k.user_email}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: 1.5, textTransform: "uppercase" }}>SUBMITTED</div>
                      <div style={{ fontSize: 13, color: T.textSec, marginTop: 2 }}>{timeAgo(k.submitted_at)}</div>
                    </div>
                  </div>

                  <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", marginBottom: 14 }}>
                    <Field label="Phone" v={k.phone}/>
                    <Field label="Country" v={k.country}/>
                    <Field label="Account Type" v={k.account_type}/>
                    <Field label="Document" v={`${k.doc_type?.toUpperCase()} (****${k.doc_last4})`}/>
                    <Field label="City" v={k.city}/>
                    <Field label="State" v={k.state}/>
                  </div>

                  {k.company_name && <div style={{ marginBottom: 10 }}><Field label="Company" v={k.company_name}/></div>}
                  <Field label="Address" v={`${k.address}, ${k.zipcode}`}/>

                  <div style={{ display: "flex", gap: 10, marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                    {k.has_file && (
                      <button onClick={() => viewDocument(k.kyc_id)} className="au-pill au-pill-sm" style={{
                        background: "var(--au-glass-strong)", border: `1px solid ${T.info}`,
                        color: T.info, fontWeight: 600,
                      }}><Paperclip size={14} /> View Document</button>
                    )}
                    <button onClick={() => approveKyc(k.kyc_id, k.user_email)} className="au-pill au-pill-sm" style={{
                      background: "var(--au-glass-strong)", border: `1px solid ${T.live}`,
                      color: T.live, fontWeight: 600,
                    }}><Check size={14} /> Approve</button>
                    <button onClick={() => rejectKyc(k.kyc_id, k.user_email)} className="au-pill au-pill-sm" style={{
                      background: "var(--au-glass-strong)", border: `1px solid ${T.danger}`,
                      color: T.danger, fontWeight: 600,
                    }}><X size={14} /> Reject</button>
                  </div>
                </div>
              ))}
            </>
          )}

          {tab === "scans" && (
            <div className="au-glass" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{
                display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1.5fr",
                padding: "14px 20px",
                background: "var(--au-glass)",
                borderBottom: `1px solid ${T.border}`,
                fontSize: 10, color: T.textMuted, fontWeight: 700,
                letterSpacing: 1.5, textTransform: "uppercase", gap: 10,
              }}>
                <span>Target</span><span>User</span><span>Risk</span><span>Status</span><span>Time</span>
              </div>
              {scans.map((s) => (
                <div key={s.id} className="row-hover" style={{
                  display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1.5fr",
                  padding: "12px 20px", borderBottom: `1px solid ${T.border}`,
                  alignItems: "center", gap: 10, fontSize: 12,
                }}>
                  <span className="au-mono" style={{ color: T.info }}>{s.target}</span>
                  <span style={{ color: T.textSec, overflow: "hidden", textOverflow: "ellipsis" }}>{s.user_email}</span>
                  <span className="au-mono" style={{
                    color: s.risk_score >= 70 ? T.danger : s.risk_score >= 40 ? T.warning : T.live,
                    fontWeight: 700, fontSize: 14,
                  }}>{s.risk_score ?? "—"}</span>
                  <span style={{ textTransform: "uppercase", color: T.textSec, fontSize: 11 }}>{String(s.status).split(".").pop()}</span>
                  <span style={{ color: T.textMuted }}>{timeAgo(s.created_at)}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "blog" && <MasterBlog />}

          {tab === "audit" && (
            <div className="au-glass" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{
                display: "grid", gridTemplateColumns: "1.2fr 2fr 1.5fr 1.2fr 1.2fr",
                padding: "14px 20px",
                background: "var(--au-glass)",
                borderBottom: `1px solid ${T.border}`,
                fontSize: 10, color: T.textMuted, fontWeight: 700,
                letterSpacing: 1.5, textTransform: "uppercase", gap: 10,
              }}>
                <span>Action</span><span>Target</span><span>User</span><span>IP</span><span>Time</span>
              </div>
              {audit.map((a) => (
                <div key={a.id} className="row-hover" style={{
                  display: "grid", gridTemplateColumns: "1.2fr 2fr 1.5fr 1.2fr 1.2fr",
                  padding: "12px 20px", borderBottom: `1px solid ${T.border}`,
                  alignItems: "center", gap: 10, fontSize: 12,
                }}>
                  <span style={{
                    color: a.action === "scan_blocked" ? T.danger : T.live,
                    fontWeight: 700, textTransform: "uppercase", fontSize: 11,
                  }}>{a.action}</span>
                  <span className="au-mono" style={{ color: T.text }}>{a.target}</span>
                  <span style={{ color: T.textSec, overflow: "hidden", textOverflow: "ellipsis" }}>{a.user_email || "—"}</span>
                  <span className="au-mono" style={{ color: T.textMuted, fontSize: 11 }}>{a.ip_address}</span>
                  <span style={{ color: T.textMuted }}>{timeAgo(a.created_at)}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "settings" && (
            <div style={{ maxWidth: 720 }}>
              <div className="au-glass" style={{ padding: 24, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 16 }}>Admin Account</div>
                <div style={{ display: "grid", gap: 14, gridTemplateColumns: "1fr 1fr" }}>
                  <Field label="Username" v="aegismaster"/>
                  <Field label="Role" v="Owner · God Mode"/>
                  <Field label="Master Panel URL" v="/mascop-2k26"/>
                  <Field label="Token expiry" v="8 hours"/>
                </div>
                <div style={{
                  display: "flex", gap: 10, marginTop: 20, paddingTop: 20,
                  borderTop: `1px solid ${T.border}`, flexWrap: "wrap",
                }}>
                  <button onClick={() => {
                    if (window.confirm("Logout karke login page pe Forgot Password se reset karenge. Continue?")) {
                      clearAllTokens();
                      nav("/mascop-2k26");
                    }
                  }} className="au-pill au-pill-sm" style={{
                    background: T.accentDim, border: `1px solid ${T.accent}`,
                    color: T.accent, fontWeight: 600,
                  }}><KeyRound size={14} /> Change Password</button>
                  <button onClick={() => loadAll(true)} className="au-pill au-pill-sm" style={{
                    background: "var(--au-glass-strong)", border: `1px solid ${T.info}`,
                    color: T.info, fontWeight: 600,
                  }}><RotateCw size={14} /> Refresh All Data</button>
                  <button onClick={() => {
                    navigator.clipboard.writeText(window.location.origin + "/mascop-2k26");
                    toast.success("Panel URL copied");
                  }} className="au-pill au-pill-sm" style={{
                    background: "var(--au-glass-strong)", border: `1px solid ${T.live}`,
                    color: T.live, fontWeight: 600,
                  }}><Copy size={14} /> Copy Panel URL</button>
                  <button onClick={() => window.open("/api/health", "_blank")} className="au-pill au-pill-sm" style={{
                    background: "var(--au-glass)", border: `1px solid ${T.border}`,
                    color: T.textSec, fontWeight: 600,
                  }}><Stethoscope size={14} /> Backend Health</button>
                </div>
              </div>

              <div className="au-glass" style={{ padding: 24, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 16 }}>System Stats</div>
                <div style={{ display: "grid", gap: 14, gridTemplateColumns: "1fr 1fr 1fr" }}>
                  <Field label="Total Users" v={stats?.users?.total}/>
                  <Field label="Email Verified" v={stats?.users?.verified}/>
                  <Field label="KYC Approved" v={stats?.kyc?.approved}/>
                  <Field label="Total Scans" v={stats?.scans?.total}/>
                  <Field label="Scans Completed" v={stats?.scans?.completed}/>
                  <Field label="Scans Failed" v={stats?.scans?.failed}/>
                  <Field label="Banned Users" v={stats?.users?.banned}/>
                  <Field label="Blocked Attempts" v={stats?.audit?.blocked_total}/>
                  <Field label="Active Last 24h" v={liveStats?.active_24h}/>
                </div>
              </div>

              <div className="au-glass" style={{
                padding: 20,
                border: `1px solid ${T.warning}`,
                fontSize: 13, color: T.warning, lineHeight: 1.6,
                display: "flex", gap: 12, alignItems: "flex-start",
              }}>
                <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>This panel grants full control over the platform. All actions are logged.
                If you suspect unauthorized access, change the master password immediately via the Forgot Password flow.</span>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}

// Small left-arrow glyph so the collapse toggle stays icon-based (lucide ArrowLeft).
function ArrowLeftGlyph() {
  return <ArrowRight size={16} style={{ transform: "rotate(180deg)" }} />;
}

// Activity feed marker — colored dot matching the legacy per-item color.
function ActivityDot({ color }) {
  return <span style={{ width: 8, height: 8, borderRadius: "50%", background: color || "var(--au-muted)", display: "inline-block" }} />;
}

function ChartCard({ title, children }) {
  return (
    <div className="au-glass" style={{ padding: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 14, letterSpacing: 0.3 }}>{title}</div>
      {children}
    </div>
  );
}

// Polished risk donut: gradient + rounded, separated segments, a live center
// total (hovered segment's value/name when hovering), hover dimming, and a
// custom legend showing each risk's count and share.
function RiskDonut({ data }) {
  const [active, setActive] = useState(-1);
  const total = data.reduce((s, r) => s + (r.value || 0), 0);
  return (
    <div>
      <div style={{ position: "relative", width: "100%", height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <defs>
              {data.map((r, i) => (
                <linearGradient key={i} id={`riskG${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={r.color} stopOpacity={0.98} />
                  <stop offset="100%" stopColor={r.color} stopOpacity={0.7} />
                </linearGradient>
              ))}
            </defs>
            <Pie
              data={data} cx="50%" cy="50%"
              innerRadius={64} outerRadius={92}
              dataKey="value" nameKey="name"
              paddingAngle={3} cornerRadius={7}
              stroke="none" startAngle={90} endAngle={-270}
              isAnimationActive={true}
              onMouseEnter={(_, i) => setActive(i)}
              onMouseLeave={() => setActive(-1)}
            >
              {data.map((r, i) => (
                <Cell key={i} fill={`url(#riskG${i})`} cursor="pointer"
                  opacity={active === -1 || active === i ? 1 : 0.32} />
              ))}
            </Pie>
            <Tooltip
              cursor={false}
              contentStyle={{ background: CHART.panel, border: `1px solid ${CHART.grid}`, borderRadius: 8, color: CHART.text, fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}
              formatter={(v, n) => [`${v} scan${v === 1 ? "" : "s"} · ${total ? Math.round((v / total) * 100) : 0}%`, n]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div style={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", pointerEvents: "none",
        }}>
          <div className="au-mono" style={{ fontSize: 30, fontWeight: 700, color: active >= 0 ? data[active].color : T.text, lineHeight: 1, transition: "color .18s ease" }}>
            {active >= 0 ? data[active].value : total}
          </div>
          <div style={{ fontSize: 9.5, letterSpacing: 1.5, textTransform: "uppercase", color: T.textMuted, marginTop: 5, fontWeight: 600 }}>
            {active >= 0 ? data[active].name : "Total Scans"}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
        {data.map((r, i) => {
          const pct = total ? Math.round((r.value / total) * 100) : 0;
          return (
            <div key={i}
              onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(-1)}
              style={{ display: "flex", alignItems: "center", gap: 10, opacity: active === -1 || active === i ? 1 : 0.5, transition: "opacity .18s ease" }}>
              <span style={{ width: 9, height: 9, borderRadius: 3, background: r.color, flexShrink: 0, boxShadow: `0 0 6px ${r.color}88` }} />
              <span style={{ fontSize: 12, color: T.textSec, flex: 1 }}>{r.name}</span>
              <span className="au-mono" style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{r.value}</span>
              <span className="au-mono" style={{ fontSize: 11, color: T.textMuted, width: 40, textAlign: "right" }}>{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontSize: 13 }}>{text}</div>
  );
}

function Badge({ color, icon, text }) {
  return (
    <span className="au-chip au-chip-status" style={{
      padding: "3px 8px", color, fontSize: 10, fontWeight: 600,
      borderRadius: 4, letterSpacing: 0.5, gap: 4,
    }}>{icon}{text}</span>
  );
}

// Compact icon-only action button: neutral glass chip with a color-coded icon.
// On hover the border takes the action color, the fill brightens, and a small
// styled tooltip (from `title`) rises above the button.
function SmallBtn({ children, onClick, color, title }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      aria-label={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32,
        background: hover ? "var(--au-glass-strong)" : "var(--au-glass)",
        border: `1px solid ${hover ? color : "var(--au-hair)"}`,
        borderRadius: 8, cursor: "pointer", color,
        transition: "background .14s ease, border-color .14s ease",
      }}
    >
      {children}
      {hover && title && (
        <span style={{
          position: "absolute", bottom: "calc(100% + 7px)", left: "50%",
          transform: "translateX(-50%)", whiteSpace: "nowrap",
          background: "var(--au-bg-elevated)", color: "var(--au-text)",
          border: "1px solid var(--au-hair)", borderRadius: 6,
          padding: "4px 9px", fontSize: 11, fontWeight: 600, letterSpacing: 0.2,
          boxShadow: "0 6px 18px rgba(0,0,0,0.45)", pointerEvents: "none", zIndex: 30,
        }}>{title}</span>
      )}
    </button>
  );
}
